import React, { useState, useEffect } from 'react';
import { sponsorshipService } from '../../../Services/sponsorshipService';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Badge } from '../../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { 
  FileText, 
  BarChart3, 
  RefreshCw, 
  AlertCircle, 
  TrendingUp, 
  Calendar, 
  Users, 
  MapPin, 
  CheckCircle, 
  Clock, 
  XCircle,
  Package,
  AlertTriangle
} from 'lucide-react';

/**
 * Admin Reports Page
 * Provides access to sponsorship reporting with latest API implementation
 */
const AdminReports = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Data states
  const [sponsors, setSponsors] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loadingSponsors, setLoadingSponsors] = useState(true);
  const [loadingContracts, setLoadingContracts] = useState(true);
  
  // Report data states
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [contractReport, setContractReport] = useState(null);
  const [summaryReport, setSummaryReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  
  // Filter states
  const [selectedSponsor, setSelectedSponsor] = useState('');
  const [selectedContract, setSelectedContract] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(getISOWeek(new Date()));
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: '',
  });
  const [inventoryFilters, setInventoryFilters] = useState({
    coverage_scope: 'national',
    territories: [],
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  // Load sponsors and contracts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingSponsors(true);
        setLoadingContracts(true);
        
        const [sponsorsResponse, contractsResponse] = await Promise.all([
          sponsorshipService.sponsors.list({ is_active: true, limit: 1000 }),
          sponsorshipService.contracts.list({ limit: 1000 })
        ]);
        
        setSponsors(sponsorsResponse.sponsors || []);
        setContracts(contractsResponse.contracts || []);
        
        // Auto-select first sponsor and contract
        if (sponsorsResponse.sponsors?.length > 0) {
          setSelectedSponsor(sponsorsResponse.sponsors[0]._id);
        }
        if (contractsResponse.contracts?.length > 0) {
          setSelectedContract(contractsResponse.contracts[0]._id);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoadingSponsors(false);
        setLoadingContracts(false);
      }
    };
    loadData();
  }, []);

  // Helper function to get ISO week number
  function getISOWeek(date) {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
  }

  // Fetch weekly delivery report
  const fetchWeeklyReport = async () => {
    if (!selectedSponsor) {
      setError('Please select a sponsor');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await sponsorshipService.reports.getWeeklyDelivery(
        selectedSponsor,
        selectedYear,
        selectedWeek
      );
      setWeeklyReport(data);
    } catch (err) {
      setError(err.message || 'Failed to load weekly report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch contract validation report
  const fetchContractReport = async (includeWeekly = false) => {
    if (!selectedContract) {
      setError('Please select a contract');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await sponsorshipService.reports.getContractValidation(
        selectedContract,
        includeWeekly
      );
      setContractReport(data);
    } catch (err) {
      setError(err.message || 'Failed to load contract report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch sponsor summary report
  const fetchSummaryReport = async () => {
    if (!selectedSponsor) {
      setError('Please select a sponsor');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await sponsorshipService.reports.getSponsorSummary(
        selectedSponsor,
        dateRange.start_date,
        dateRange.end_date
      );
      setSummaryReport(data);
    } catch (err) {
      setError(err.message || 'Failed to load summary report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch inventory utilization report
  const fetchInventoryReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        coverage_scope: inventoryFilters.coverage_scope,
        start_date: inventoryFilters.start_date,
        end_date: inventoryFilters.end_date,
      };
      if (inventoryFilters.coverage_scope === 'territory' && inventoryFilters.territories.length > 0) {
        params.territories = inventoryFilters.territories;
      }
      const data = await sponsorshipService.reports.getInventoryUtilization(params);
      setInventoryReport(data);
    } catch (err) {
      setError(err.message || 'Failed to load inventory report');
    } finally {
      setLoading(false);
    }
  };

  // Get status badge configuration
  const getStatusBadge = (status) => {
    const configs = {
      completed: { variant: 'default', icon: CheckCircle, label: 'Completed' },
      pending: { variant: 'secondary', icon: Clock, label: 'Pending' },
      unfilled: { variant: 'destructive', icon: XCircle, label: 'Unfilled' },
      on_track: { variant: 'default', icon: CheckCircle, label: 'On Track' },
      behind: { variant: 'destructive', icon: AlertTriangle, label: 'Behind' },
      active: { variant: 'default', icon: CheckCircle, label: 'Active' },
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Metric Card Component
  const MetricCard = ({ icon: Icon, label, value, description, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      orange: 'bg-orange-100 text-orange-600',
      purple: 'bg-purple-100 text-purple-600',
    };
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground">{label}</p>
              <p className="text-2xl font-bold mt-1">{value}</p>
              {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render Weekly Delivery Report
  const renderWeeklyReport = () => {
    if (!weeklyReport) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Weekly Delivery Report</h2>
            <p className="text-muted-foreground mt-1">
              Week {selectedWeek}, {selectedYear}
            </p>
          </div>
          {getStatusBadge(weeklyReport.delivery_status)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            icon={Users}
            label="Unique Individuals"
            value={weeklyReport.unique_individuals?.toLocaleString() || 0}
            description="Assigned access this week only"
            color="blue"
          />
          <MetricCard
            icon={Calendar}
            label="Week Period"
            value={`${new Date(weeklyReport.week_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(weeklyReport.week_end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
            description="Monday to Sunday"
            color="green"
          />
          <MetricCard
            icon={MapPin}
            label="Geographic Scope"
            value={weeklyReport.geographic_scope}
            description={weeklyReport.territories?.length > 0 ? weeklyReport.territories.join(', ') : 'All regions'}
            color="purple"
          />
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">
              <strong>Privacy Note:</strong> Uniqueness is calculated within this week only. 
              Individuals may be assigned access again in subsequent weeks. No individual-level data is tracked.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Contract Validation Report
  const renderContractReport = () => {
    if (!contractReport) return null;

    const deliveryProgress = (contractReport.total_weeks_delivered / contractReport.total_weeks_purchased) * 100;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{contractReport.sponsor_name}</h2>
            <p className="text-muted-foreground mt-1">Contract Validation Report</p>
          </div>
          {getStatusBadge(contractReport.placement_window_status)}
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">Delivery Progress</span>
            <span className="text-muted-foreground">
              {contractReport.total_weeks_delivered} / {contractReport.total_weeks_purchased} weeks
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-primary h-3 rounded-full transition-all"
              style={{ width: `${deliveryProgress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">{deliveryProgress.toFixed(1)}% completed</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={Package}
            label="Weeks Purchased"
            value={contractReport.total_weeks_purchased}
            color="blue"
          />
          <MetricCard
            icon={CheckCircle}
            label="Weeks Delivered"
            value={contractReport.total_weeks_delivered}
            color="green"
          />
          <MetricCard
            icon={Clock}
            label="Weeks Remaining"
            value={contractReport.total_weeks_remaining}
            color="orange"
          />
          <MetricCard
            icon={TrendingUp}
            label="Delivery Rate"
            value={`${deliveryProgress.toFixed(1)}%`}
            color="purple"
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Placement Window
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Start Date</p>
                <p className="font-medium">
                  {new Date(contractReport.placement_window_start).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">End Date</p>
                <p className="font-medium">
                  {new Date(contractReport.placement_window_end).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Geographic Scope
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="capitalize">
                {contractReport.geographic_scope}
              </Badge>
              {contractReport.territories?.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {contractReport.territories.join(', ')}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Sponsor Summary Report
  const renderSummaryReport = () => {
    if (!summaryReport) return null;

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">{summaryReport.sponsor_name}</h2>
          <p className="text-muted-foreground mt-1">Comprehensive Sponsor Summary</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={Package}
            label="Total Contracts"
            value={summaryReport.summary?.total_contracts || 0}
            color="blue"
          />
          <MetricCard
            icon={CheckCircle}
            label="Weeks Delivered"
            value={summaryReport.summary?.total_weeks_delivered || 0}
            color="green"
          />
          <MetricCard
            icon={Clock}
            label="Weeks Remaining"
            value={summaryReport.summary?.total_weeks_remaining || 0}
            color="orange"
          />
          <MetricCard
            icon={TrendingUp}
            label="Delivery Rate"
            value={`${summaryReport.summary?.delivery_rate || 0}%`}
            color="purple"
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Active Contracts</CardTitle>
            <CardDescription>All contracts for {summaryReport.sponsor_name}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contract ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Purchased</TableHead>
                  <TableHead className="text-right">Delivered</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead>Placement Window</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summaryReport.contracts?.map((contract) => (
                  <TableRow key={contract.contract_id}>
                    <TableCell className="font-mono text-sm">
                      {contract.contract_id.slice(0, 8)}...
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(contract.status)}
                    </TableCell>
                    <TableCell className="text-right">{contract.weeks_purchased}</TableCell>
                    <TableCell className="text-right font-medium text-green-600">
                      {contract.weeks_delivered}
                    </TableCell>
                    <TableCell className="text-right">{contract.weeks_remaining}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(contract.placement_window.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - 
                      {new Date(contract.placement_window.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Inventory Utilization Report
  const renderInventoryReport = () => {
    if (!inventoryReport) return null;

    const pacingConfig = {
      on_track: { color: 'text-green-600', label: 'On Track' },
      ahead: { color: 'text-blue-600', label: 'Ahead of Schedule' },
      behind: { color: 'text-red-600', label: 'Behind Schedule' },
    };
    const pacing = pacingConfig[inventoryReport.pacing_status] || pacingConfig.on_track;

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Inventory Utilization Report</h2>
          <p className="text-muted-foreground mt-1">{inventoryReport.reporting_period}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={Package}
            label="Total Capacity"
            value={`${inventoryReport.total_capacity_weeks?.toLocaleString() || 0} weeks`}
            color="blue"
          />
          <MetricCard
            icon={CheckCircle}
            label="Weeks Allocated"
            value={inventoryReport.weeks_allocated?.toLocaleString() || 0}
            color="green"
          />
          <MetricCard
            icon={Clock}
            label="Weeks Remaining"
            value={inventoryReport.weeks_remaining?.toLocaleString() || 0}
            color="orange"
          />
          <MetricCard
            icon={TrendingUp}
            label="Utilization Rate"
            value={`${inventoryReport.utilization_percent || 0}%`}
            color="purple"
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Delivery Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Expected Delivery</p>
                <p className="text-2xl font-bold">{inventoryReport.expected_delivery_percent || 0}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Actual Delivery</p>
                <p className="text-2xl font-bold">{inventoryReport.actual_delivery_percent || 0}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pacing Status</p>
                <p className={`text-2xl font-bold ${pacing.color}`}>{pacing.label}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Coverage Scope
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="capitalize">
                {inventoryReport.coverage_scope}
              </Badge>
              {inventoryReport.territories?.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {inventoryReport.territories.join(', ')}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Get selected sponsor name
  const getSelectedSponsorName = () => {
    const sponsor = sponsors.find(s => s._id === selectedSponsor);
    return sponsor?.name || 'Unknown Sponsor';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Sponsorship Reporting</h1>
        <p className="text-muted-foreground mt-1">Access-first validation reporting for the Guided Plan</p>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="summary">Sponsor Summary</TabsTrigger>
          <TabsTrigger value="contract">Contract Validation</TabsTrigger>
          <TabsTrigger value="weekly">Weekly Delivery</TabsTrigger>
          <TabsTrigger value="inventory">Inventory Utilization</TabsTrigger>
        </TabsList>

        {/* Sponsor Summary Tab */}
        <TabsContent value="summary" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="summary-sponsor">Sponsor</Label>
                  {loadingSponsors ? (
                    <div className="text-sm text-muted-foreground">Loading...</div>
                  ) : (
                    <select
                      id="summary-sponsor"
                      className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={selectedSponsor}
                      onChange={(e) => setSelectedSponsor(e.target.value)}
                    >
                      {sponsors.map((sponsor) => (
                        <option key={sponsor._id} value={sponsor._id}>
                          {sponsor.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="summary-start">Start Date (Optional)</Label>
                  <Input
                    id="summary-start"
                    type="date"
                    value={dateRange.start_date}
                    onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="summary-end">End Date (Optional)</Label>
                  <Input
                    id="summary-end"
                    type="date"
                    value={dateRange.end_date}
                    onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                  />
                </div>
              </div>
              <Button onClick={fetchSummaryReport} disabled={loading || !selectedSponsor}>
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                Generate Report
              </Button>
            </CardContent>
          </Card>
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ) : (
            renderSummaryReport()
          )}
        </TabsContent>

        {/* Contract Validation Tab */}
        <TabsContent value="contract" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contract-select">Contract</Label>
                {loadingContracts ? (
                  <div className="text-sm text-muted-foreground">Loading...</div>
                ) : (
                  <select
                    id="contract-select"
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={selectedContract}
                    onChange={(e) => setSelectedContract(e.target.value)}
                  >
                    {contracts.map((contract) => (
                      <option key={contract._id} value={contract._id}>
                        {contract._id.slice(0, 8)}... - {contract.status}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <Button onClick={() => fetchContractReport(false)} disabled={loading || !selectedContract}>
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                Generate Report
              </Button>
            </CardContent>
          </Card>
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ) : (
            renderContractReport()
          )}
        </TabsContent>

        {/* Weekly Delivery Tab */}
        <TabsContent value="weekly" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="weekly-sponsor">Sponsor</Label>
                  {loadingSponsors ? (
                    <div className="text-sm text-muted-foreground">Loading...</div>
                  ) : (
                    <select
                      id="weekly-sponsor"
                      className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={selectedSponsor}
                      onChange={(e) => setSelectedSponsor(e.target.value)}
                    >
                      {sponsors.map((sponsor) => (
                        <option key={sponsor._id} value={sponsor._id}>
                          {sponsor.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weekly-year">Year</Label>
                  <select
                    id="weekly-year"
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  >
                    {[2024, 2025, 2026, 2027].map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weekly-week">Week</Label>
                  <select
                    id="weekly-week"
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={selectedWeek}
                    onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 52 }, (_, i) => i + 1).map((week) => (
                      <option key={week} value={week}>
                        Week {week}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Button onClick={fetchWeeklyReport} disabled={loading || !selectedSponsor}>
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                Generate Report
              </Button>
            </CardContent>
          </Card>
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ) : (
            renderWeeklyReport()
          )}
        </TabsContent>

        {/* Inventory Utilization Tab */}
        <TabsContent value="inventory" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="inventory-scope">Coverage Scope</Label>
                  <select
                    id="inventory-scope"
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={inventoryFilters.coverage_scope}
                    onChange={(e) => setInventoryFilters({ ...inventoryFilters, coverage_scope: e.target.value })}
                  >
                    <option value="national">National</option>
                    <option value="territory">Territory</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inventory-start">Start Date</Label>
                  <Input
                    id="inventory-start"
                    type="date"
                    value={inventoryFilters.start_date}
                    onChange={(e) => setInventoryFilters({ ...inventoryFilters, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inventory-end">End Date</Label>
                  <Input
                    id="inventory-end"
                    type="date"
                    value={inventoryFilters.end_date}
                    onChange={(e) => setInventoryFilters({ ...inventoryFilters, end_date: e.target.value })}
                  />
                </div>
              </div>
              <Button onClick={fetchInventoryReport} disabled={loading}>
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
                Generate Report
              </Button>
            </CardContent>
          </Card>
          {loading ? (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ) : (
            renderInventoryReport()
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReports;
